import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  FileText,
  Upload,
  Eye,
  Download,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  DollarSign,
  Calendar,
  Search,
  FileImage,
  Loader2,
  Brain,
  Zap,
  Shield,
  Target,
  TrendingUp,
  X,
  Plus,
  GitCompare,
  Lightbulb,
  PlayCircle,
  Trash2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ContractIntelligencePage = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  // Estados principais
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForComparison, setSelectedForComparison] = useState([]);

  // Estados das abas
  const [activeTab, setActiveTab] = useState('upload');
  const [uploadSuccess, setUploadSuccess] = useState(null);

  useEffect(() => {
    loadUploadedFiles();
    loadAnalyses();
  }, []);

  useEffect(() => {
    if (activeTab === 'files') {
      loadUploadedFiles();
    } else if (activeTab === 'results') {
      loadAnalyses();
    }
  }, [activeTab]);

  const loadUploadedFiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/contract-intelligence/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUploadedFiles(response.data);
    } catch (error) {
      console.error('Erro ao carregar arquivos:', error);
    }
  };

  const loadAnalyses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/contract-intelligence/analyses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalyses(response.data);
    } catch (error) {
      console.error('Erro ao carregar análises:', error);
    }
  };

  const handleFileUpload = async (event) => {
    console.log('handleFileUpload chamado', event.target.files);
    const files = Array.from(event.target.files);
    if (files.length === 0) {
      console.log('Nenhum arquivo selecionado');
      return;
    }

    // Validar tamanho dos arquivos ANTES de enviar
    const maxSize = 30 * 1024 * 1024; // 30MB
    const invalidFiles = files.filter(file => file.size > maxSize);
    
    if (invalidFiles.length > 0) {
      const fileNames = invalidFiles.map(f => `${f.name} (${(f.size / (1024 * 1024)).toFixed(2)}MB)`).join(', ');
      toast.error(`Arquivo(s) muito grande(s): ${fileNames}. Tamanho máximo: 30MB`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/contract-intelligence/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`${files.length} arquivo(s) enviado(s) com sucesso!`);
      loadUploadedFiles();
      setUploadSuccess({
        count: files.length,
        filename: files.length === 1 ? files[0].name : null
      });
      
      setTimeout(() => {
        setActiveTab('files');
        setUploadSuccess(null);
      }, 2000);
      
      // Auto-analisar se apenas um arquivo
      // if (files.length === 1 && response.data.files?.length > 0) {
      //   await analyzeDocument(response.data.files[0].id);
      // }

    } catch (error) {
      console.error('Erro no upload:', error);
      toast.error(error.response?.data?.detail || 'Erro ao fazer upload dos arquivos');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteFile = async (fileId, filename) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${filename}"?`)) return;

    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/contract-intelligence/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`"${filename}" excluído com sucesso!`);
      loadUploadedFiles();
    } catch (error) {
      console.error('Erro ao excluir arquivo:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir arquivo');
      loadUploadedFiles();
    }
  };

  const analyzeDocument = async (fileId) => {
    setIsAnalyzing(true);
    setAnalysisResults(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/contract-intelligence/analyze/${fileId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAnalysisResults(response.data);
      setActiveTab('results');
      toast.success('Análise concluída com sucesso!');
      loadAnalyses();

    } catch (error) {
      console.error('Erro na análise:', error);
      toast.error('Erro ao analisar documento');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const compareDocuments = async () => {
    if (selectedForComparison.length < 2) {
      toast.error('Selecione pelo menos 2 documentos para comparação');
      return;
    }

    setIsAnalyzing(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/contract-intelligence/compare`, {
        file_ids: selectedForComparison
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAnalysisResults(response.data);
      setActiveTab('results');
      toast.success('Comparação concluída com sucesso!');

    } catch (error) {
      console.error('Erro na comparação:', error);
      toast.error('Erro ao comparar documentos');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getRiskBadge = (riskLevel) => {
    const variants = {
      'ALTO': { variant: 'destructive', label: 'Alto Risco' },
      'MEDIO': { variant: 'secondary', label: 'Médio Risco' },
      'BAIXO': { variant: 'outline', label: 'Baixo Risco' }
    };
    
    const config = variants[riskLevel] || { variant: 'outline', label: 'Não avaliado' };
    
    return (
      <Badge variant={config.variant} className="text-xs">
        {config.label}
      </Badge>
    );
  };

  const getFileIcon = (filename) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(extension)) return <FileText className="h-5 w-5 text-red-500" />;
    if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) return <FileImage className="h-5 w-5 text-blue-500" />;
    return <FileText className="h-5 w-5 text-gray-500" />;
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-6">
        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
          <Brain className="h-6 w-6 text-blue-600" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Contract Intelligence</h1>
          <p className="text-gray-600">Análise inteligente de contratos e documentos com IA</p>
        </div>
      </div>

      {/* Tabs Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="upload" className="flex items-center space-x-2">
            <Upload className="h-4 w-4" />
            <span>Upload</span>
          </TabsTrigger>
          <TabsTrigger value="files" className="flex items-center space-x-2">
            <FileText className="h-4 w-4" />
            <span>Documentos</span>
          </TabsTrigger>
          <TabsTrigger value="results" className="flex items-center space-x-2">
            <Eye className="h-4 w-4" />
            <span>Resultados</span>
          </TabsTrigger>
          <TabsTrigger value="info" className="flex items-center space-x-2">
            <Lightbulb className="h-4 w-4" />
            <span>Como Usar</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab Content: Upload */}
        <TabsContent value="upload" className="space-y-6">
          {uploadSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 flex items-center">
              <CheckCircle className="h-5 w-5 mr-2 text-green-600 shrink-0" />
              <span>
                {uploadSuccess.count > 1
                  ? `${uploadSuccess.count} arquivos enviados com sucesso! Redirecionando...`
                  : `Arquivo "${uploadSuccess.filename}" enviado com sucesso! Redirecionando...`}
              </span>
            </div>
          )}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Upload className="h-5 w-5 mr-2 text-blue-600" />
                Upload de Documentos
              </CardTitle>
              <CardDescription>
                Faça upload de contratos, PDFs ou imagens para análise inteligente
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Upload Area */}
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">
                  Clique para fazer upload ou arraste arquivos aqui
                </h3>
                <p className="text-gray-500 mb-2">
                  📎 Formatos aceitos: PDF, DOC, DOCX
                </p>
                <p className="text-sm text-gray-400 mb-4">
                  Tamanho máximo: 30MB por arquivo
                </p>
                <Button 
                  variant="outline" 
                  disabled={isUploading}
                  className="mx-auto"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Selecionar Arquivos
                    </>
                  )}
                </Button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
                className="hidden"
              />

              {/* Capacidades da IA */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <Zap className="h-8 w-8 text-blue-600" />
                      <div>
                        <h4 className="font-semibold text-blue-900">OCR Avançado</h4>
                        <p className="text-xs text-blue-700">Extrai texto de PDFs e imagens</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <Brain className="h-8 w-8 text-green-600" />
                      <div>
                        <h4 className="font-semibold text-green-900">Análise Semântica</h4>
                        <p className="text-xs text-green-700">Compreende contexto e significado</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <Shield className="h-8 w-8 text-red-600" />
                      <div>
                        <h4 className="font-semibold text-red-900">Detecção de Riscos</h4>
                        <p className="text-xs text-red-700">Identifica cláusulas problemáticas</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Content: Files */}
        <TabsContent value="files" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-blue-600" />
                    Documentos Enviados
                  </CardTitle>
                  <CardDescription>
                    Gerencie e analise seus documentos
                  </CardDescription>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCompareMode(!compareMode)}
                  >
                    <GitCompare className="h-4 w-4 mr-2" />
                    {compareMode ? 'Cancelar Comparação' : 'Comparar Documentos'}
                  </Button>
                  {compareMode && selectedForComparison.length >= 2 && (
                    <Button
                      onClick={compareDocuments}
                      disabled={isAnalyzing}
                      size="sm"
                    >
                      {isAnalyzing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Target className="h-4 w-4 mr-2" />
                      )}
                      Comparar ({selectedForComparison.length})
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {uploadedFiles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {uploadedFiles.map((file) => (
                    <Card key={file.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            {compareMode && (
                              <input
                                type="checkbox"
                                checked={selectedForComparison.includes(file.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedForComparison([...selectedForComparison, file.id]);
                                  } else {
                                    setSelectedForComparison(selectedForComparison.filter(id => id !== file.id));
                                  }
                                }}
                                className="rounded border-gray-300"
                              />
                            )}
                            {getFileIcon(file.filename)}
                            <div>
                              <h4 className="font-medium text-sm truncate max-w-[150px]" title={file.filename}>
                                {file.filename}
                              </h4>
                              <p className="text-xs text-gray-500">
                                {new Date(file.uploaded_at).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Status da Análise */}
                        <div className="mb-3">
                          {file.analysis_status === 'COMPLETED' && (
                            <div className="flex items-center space-x-1">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span className="text-xs text-green-600">Analisado</span>
                              {file.risk_level && getRiskBadge(file.risk_level)}
                            </div>
                          )}
                          {file.analysis_status === 'PENDING' && (
                            <div className="flex items-center space-x-1">
                              <Clock className="h-4 w-4 text-yellow-500" />
                              <span className="text-xs text-yellow-600">Pendente</span>
                            </div>
                          )}
                          {!file.analysis_status && (
                            <div className="flex items-center space-x-1">
                              <AlertTriangle className="h-4 w-4 text-gray-500" />
                              <span className="text-xs text-gray-500">Não analisado</span>
                            </div>
                          )}
                        </div>

                        {/* Ações */}
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => analyzeDocument(file.id)}
                            disabled={isAnalyzing}
                            className="flex-1"
                          >
                            {isAnalyzing ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Brain className="h-3 w-3" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`${API}/api/contract-intelligence/files/${file.id}/download`, '_blank')}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteFile(file.id, file.filename)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum documento enviado ainda</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setActiveTab('upload')}
                  >
                    Fazer Upload
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Content: Results */}
        <TabsContent value="results" className="space-y-6">
          {analysisResults ? (
            <div className="space-y-6">
              {/* Resumo da Análise */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Brain className="h-5 w-5 mr-2 text-blue-600" />
                    Resultado da Análise
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="p-4 text-center">
                        <TrendingUp className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                        <div className="text-2xl font-bold text-blue-900">
                          {analysisResults.confidence_score || 'N/A'}%
                        </div>
                        <div className="text-sm text-blue-700">Confiança</div>
                      </CardContent>
                    </Card>

                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="p-4 text-center">
                        <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                        <div className="text-2xl font-bold text-green-900">
                          {analysisResults.extracted_entities?.length || 0}
                        </div>
                        <div className="text-sm text-green-700">Entidades Extraídas</div>
                      </CardContent>
                    </Card>

                    <Card className="bg-red-50 border-red-200">
                      <CardContent className="p-4 text-center">
                        <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                        <div className="text-2xl font-bold text-red-900">
                          {analysisResults.risks?.length || 0}
                        </div>
                        <div className="text-sm text-red-700">Riscos Identificados</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Dados Extraídos */}
                  {analysisResults.extracted_data && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold mb-3">Dados Extraídos</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {analysisResults.extracted_data.parties && (
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <Users className="h-5 w-5 text-gray-600 mb-2" />
                            <div className="font-medium text-sm">Partes</div>
                            <div className="text-xs text-gray-600">
                              {analysisResults.extracted_data.parties.join(', ')}
                            </div>
                          </div>
                        )}
                        {analysisResults.extracted_data.dates && (
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <Calendar className="h-5 w-5 text-gray-600 mb-2" />
                            <div className="font-medium text-sm">Datas Importantes</div>
                            <div className="text-xs text-gray-600">
                              {analysisResults.extracted_data.dates.join(', ')}
                            </div>
                          </div>
                        )}
                        {analysisResults.extracted_data.values && (
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <DollarSign className="h-5 w-5 text-gray-600 mb-2" />
                            <div className="font-medium text-sm">Valores</div>
                            <div className="text-xs text-gray-600">
                              {analysisResults.extracted_data.values.join(', ')}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Riscos Identificados */}
                  {analysisResults.risks && analysisResults.risks.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold mb-3 text-red-800">Riscos Identificados</h3>
                      <div className="space-y-3">
                        {analysisResults.risks.map((risk, index) => (
                          <div key={index} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-start space-x-3">
                              <AlertTriangle className="h-5 w-5 text-red-600 mt-1" />
                              <div>
                                <h4 className="font-medium text-red-900">{risk.title}</h4>
                                <p className="text-sm text-red-700">{risk.description}</p>
                                {risk.recommendation && (
                                  <p className="text-sm text-red-600 mt-2">
                                    <strong>Recomendação:</strong> {risk.recommendation}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Resumo Executivo */}
                  {analysisResults.summary && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Resumo Executivo</h3>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-gray-700 text-sm leading-relaxed">
                          {analysisResults.summary}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <Brain className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">
                  Nenhuma análise selecionada
                </h3>
                <p className="text-gray-500 mb-4">
                  Faça upload e analise um documento para ver os resultados aqui
                </p>
                <Button onClick={() => setActiveTab('upload')}>
                  Fazer Upload
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tab Content: Info */}
        <TabsContent value="info" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Como Usar */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <PlayCircle className="h-5 w-5 mr-2 text-blue-600" />
                  Como Usar
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">1</div>
                    <div>
                      <h4 className="font-medium">Upload do Documento</h4>
                      <p className="text-sm text-gray-600">Envie contratos, PDFs ou imagens</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">2</div>
                    <div>
                      <h4 className="font-medium">Análise Automática</h4>
                      <p className="text-sm text-gray-600">IA extrai dados e identifica riscos</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">3</div>
                    <div>
                      <h4 className="font-medium">Revisão dos Resultados</h4>
                      <p className="text-sm text-gray-600">Analise dados extraídos e recomendações</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">4</div>
                    <div>
                      <h4 className="font-medium">Comparação (Opcional)</h4>
                      <p className="text-sm text-gray-600">Compare múltiplos documentos</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Problemas Resolvidos */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Target className="h-5 w-5 mr-2 text-green-600" />
                  Problemas que Resolve
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Elimina horas de leitura manual</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Reduz falhas na interpretação</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Identifica datas críticas automaticamente</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Detecta cláusulas perigosas</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Centraliza informações contratuais</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tipos de Documentos */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-purple-600" />
                  Tipos de Documentos Suportados
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="font-medium">Contratos e Acordos</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Contratos comerciais e de prestação de serviços</li>
                      <li>• NDAs e acordos de confidencialidade</li>
                      <li>• Contratos de trabalho e terceirização</li>
                      <li>• Acordos de parceria e joint venture</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-medium">Documentos Jurídicos</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Propostas e orçamentos detalhados</li>
                      <li>• Termos de uso e políticas de privacidade</li>
                      <li>• Documentos regulatórios</li>
                      <li>• Atas e deliberações</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContractIntelligencePage;